/* React Higher Order Component which fetches activity data for channel where channelId = props.channelId
    Child (InnerComponent) will only render once activity data is successfully fetched.
    child will receive props: {
        channelId: PropTypes.number,
        fatalError: PropTypes.object,
        activityStoreState: PropTypes.shape({ initialized: PropTypes.bool, activities: PropTypes.object, activitySaves: PropTypes.object, ... })
    }

*/

import React from 'react';
import PropTypes from 'prop-types';
const has = require('lodash/has');
const uuid = require('uuid');

const ActivityActions = require('../../actions/ActivityActions');
const ActivityStore = require('../../stores/activity_store_reflux');
import LoadingMainContent from '../loading/LoadingMainContent';
import ErrorBox from '../common/ErrorBox';
const ReactUtil = require('../../util/ReactUtil');

const propTypes = {
    channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    forceRefreshOnMount: PropTypes.bool, // force refresh of activity data, even if activity store already has data for the given id
    hideLoadingIcon: PropTypes.bool
};

const defaultProps = {
    forceRefreshOnMount: false,
    hideLoadingIcon: false
};

export default function withChannelActivities(WrappedComponent) {
    class WithChannelActivities extends React.Component {
        constructor(props) {
            super(props);

            this.state = {
                activityStoreState: ActivityStore.getStateChannel(props.channelId),
                getActionId: null,
                fatalError: null
            }

            this._getActs = this._getActs.bind(this);
            this.unsubscribes = [ActivityStore.listen(this.onActivityStoreChange.bind(this))];
        }

        componentDidMount() {
            if (!this.state.activityStoreState.initialized || this.props.forceRefreshOnMount) {
                this._getActs(this.props.channelId);
            }
        }

        componentDidUpdate(prevProps) {
            if (this.props.channelId !== prevProps.channelId) {
                const activityStoreState = ActivityStore.getStateChannel(this.props.channelId);
                if (!activityStoreState.initialized) {
                    this._getActs(this.props.channelId);
                }

                this.setState({ activityStoreState: activityStoreState });
            }
        }

        componentWillUnmount() {
            this.unsubscribes.forEach(u => u());
        }

        _getActs(channelId) {
            const actionId = uuid.v4();
            this.setState({ getActionId: actionId }, () => {
                ActivityActions.getChannelActivities(actionId, { channelId: channelId });
            });
        }

        onActivityStoreChange(newState) {
            let additionalState = {};
            if (newState.action.id === this.state.getActionId) {
                additionalState = { ...additionalState, getActionId: null };
                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }

            }

            this.setState({
                activityStoreState: ActivityStore.getStateChannel(this.props.channelId),
                ...additionalState
            });
        }

        render() {
            if (this.state.fatalError) {
                return <ErrorBox error={this.state.fatalError} allowStackTrace={false}/>;
            }

            if (!this.state.activityStoreState.initialized) {
                if (this.props.hideLoadingIcon) {
                    return null;
                }
                else {
                    return <LoadingMainContent/>;
                }
            }

            return (
                <WrappedComponent
                    {...this.props}
                    activityStoreState={this.state.activityStoreState}
                />
            );
        }
    }

    WithChannelActivities.propTypes = propTypes;
    WithChannelActivities.defaultProps = defaultProps;
    WithChannelActivities.displayName = `WithChannelActivities(${ReactUtil.getComponentDisplayName(WrappedComponent)})`
    return WithChannelActivities;
}
